import type { JSX } from "preact";
import { useState } from "preact/hooks";
import type { CalculatorResults } from "../engine";
import { BreakEvenChart } from "./BreakEvenChart";
import { EmailGate } from "./EmailGate";
import { fmtAge, fmtMoney, fmtPct } from "./format";
import type { Household } from "./Form";

interface ResultsProps {
  results: CalculatorResults;
  household: Household;
  colaRate: number;
  primaryLongevity: number;
  spouseLongevity: number;
}

function BenefitsTable({
  results,
  who,
}: {
  results: CalculatorResults;
  who: "primary" | "spouse";
}) {
  const person = who === "primary" ? results.primary : results.spouse;
  if (!person) return null;
  const fraLabel = fmtAge(person.fra.years, person.fra.months);
  const rows: Array<{
    age: string;
    monthly: number;
    pct: number;
    cumulative: number;
  }> = [
    {
      age: "62",
      monthly: person.benefits.at62.monthlyBenefitInitial,
      pct: person.benefits.at62.pctOfPia,
      cumulative: person.benefits.at62.lifetimeCumulative,
    },
    {
      age: `FRA (${fraLabel})`,
      monthly: person.benefits.atFra.monthlyBenefitInitial,
      pct: person.benefits.atFra.pctOfPia,
      cumulative: person.benefits.atFra.lifetimeCumulative,
    },
    {
      age: "70",
      monthly: person.benefits.at70.monthlyBenefitInitial,
      pct: person.benefits.at70.pctOfPia,
      cumulative: person.benefits.at70.lifetimeCumulative,
    },
    {
      age: `Custom (${person.benefits.custom.claimAge})`,
      monthly: person.benefits.custom.monthlyBenefitInitial,
      pct: person.benefits.custom.pctOfPia,
      cumulative: person.benefits.custom.lifetimeCumulative,
    },
  ];
  return (
    <table class="tca-ss-calc__table">
      <caption class="tca-ss-calc__table-caption">
        {who === "primary" ? "Your" : "Spouse"} monthly benefit and lifetime
        total by claim age
      </caption>
      <thead>
        <tr>
          <th scope="col">Claim age</th>
          <th scope="col">Monthly benefit</th>
          <th scope="col">% of PIA</th>
          <th scope="col">Cumulative through longevity</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr>
            <th scope="row">{r.age}</th>
            <td>{fmtMoney(r.monthly)}</td>
            <td>{fmtPct(r.pct)}</td>
            <td>{fmtMoney(r.cumulative)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Results({
  results,
  household,
  colaRate,
  primaryLongevity,
  spouseLongevity,
}: ResultsProps): JSX.Element {
  const [emailGateOpen, setEmailGateOpen] = useState(false);
  const fraAgeYears =
    results.primary.fra.years + results.primary.fra.months / 12;

  const cfg = typeof window !== "undefined" ? window.tcaSsCalc : undefined;

  return (
    <section class="tca-ss-calc__results" aria-labelledby="tca-ss-results-heading">
      <h3 id="tca-ss-results-heading">Your scenario</h3>
      <p class="tca-ss-calc__results-subhead">
        Based on the assumptions you entered — your date of birth, your PIA,
        your longevity assumption, and a {fmtPct(colaRate)} annual COLA. These
        are estimates, not advice.
      </p>

      <BenefitsTable results={results} who="primary" />

      <BreakEvenChart
        pia={results.primary.pia}
        fraAgeYears={fraAgeYears}
        colaRate={colaRate}
        longevityAge={primaryLongevity}
        customClaimAge={results.primary.benefits.custom.claimAge}
      />

      {household === "couple" && results.spouse && (
        <>
          <BenefitsTable results={results} who="spouse" />
          <BreakEvenChart
            pia={results.spouse.pia}
            fraAgeYears={
              results.spouse.fra.years + results.spouse.fra.months / 12
            }
            colaRate={colaRate}
            longevityAge={spouseLongevity}
            customClaimAge={results.spouse.benefits.custom.claimAge}
          />
          {results.couple && (
            <aside class="tca-ss-calc__couple-card">
              <h4>Couples view</h4>
              <p>
                Spousal benefit at FRA (lower earner):{" "}
                <strong>{fmtMoney(results.couple.spousalBenefitAtFra)}</strong>
              </p>
              <p>
                Survivor benefit (higher earner's claim-adjusted monthly):{" "}
                <strong>{fmtMoney(results.couple.survivorBenefit)}</strong>
              </p>
              <p class="tca-ss-calc__couple-hint">
                {results.couple.survivorOptimizationHint}
              </p>
            </aside>
          )}
        </>
      )}

      <aside class="tca-ss-calc__recommendation" aria-label="Scenario summary">
        <h4>What this scenario shows</h4>
        <p>{results.recommendation}</p>
      </aside>

      <div class="tca-ss-calc__cta-block">
        <button
          type="button"
          class="tca-ss-calc__btn tca-ss-calc__btn--primary"
          onClick={() => setEmailGateOpen(true)}
        >
          Email me a copy of this report
        </button>

        {cfg?.affiliateUrl && (
          <div class="tca-ss-calc__affiliate">
            <p class="tca-ss-calc__affiliate-disclosure">
              <strong>Disclosure:</strong> Thomas Clark Advisor earns a
              commission if you sign up through the link below, at no extra cost
              to you. See{" "}
              <a href="/how-we-make-money/" rel="noopener">
                How We Make Money
              </a>
              .
            </p>
            <a
              class="tca-ss-calc__btn tca-ss-calc__btn--secondary"
              href={cfg.affiliateUrl}
              target="_blank"
              rel="sponsored noopener"
            >
              Model your full retirement plan →
            </a>
          </div>
        )}

        {cfg?.newsletterUrl && (
          <a
            class="tca-ss-calc__btn tca-ss-calc__btn--ghost"
            href={cfg.newsletterUrl}
          >
            Get the TCA newsletter
          </a>
        )}

        {cfg?.blogIndexUrl && (
          <a class="tca-ss-calc__link" href={cfg.blogIndexUrl}>
            Read related Social Security articles →
          </a>
        )}

        {cfg?.binderUrl && (
          <a class="tca-ss-calc__link" href={cfg.binderUrl}>
            Get the Just in Case Binder →
          </a>
        )}
      </div>

      <footer class="tca-ss-calc__meta">
        <p>
          Engine v{results.meta.engineVersion} · FRA table verified{" "}
          {results.meta.fraTableVerified} · Bend points verified{" "}
          {results.meta.bendPointsVerified} · COLA defaults verified{" "}
          {results.meta.colaDefaultVerified}.
        </p>
      </footer>

      {emailGateOpen && (
        <EmailGate
          results={results}
          colaRate={colaRate}
          household={household}
          primaryLongevity={primaryLongevity}
          spouseLongevity={spouseLongevity}
          onClose={() => setEmailGateOpen(false)}
        />
      )}
    </section>
  );
}
